import { api, ensureApiAuthenticated } from '../services/api';

export interface CkTradeinParams {
  payout?: 'cash' | 'credit';
  min_advantage?: number;
  min_ck_price?: number;
  top_n?: number;
}

export interface CkTradeinItem {
  rank: number;
  mtgjson_uuid: string;
  name: string;
  set_name: string | null;
  card_number: string | null;
  condition: string | null;
  printing: string | null;
  is_foil: boolean;
  language: string | null;
  copy_count: number;
  locations: string[];
  ck_buylist_nm: number | null;
  ck_cash: number | null;
  ck_credit: number | null;
  manapool_list: number | null;
  manapool_net: number | null;
  manapool_price_source: string;
  advantage_cash: number | null;
  advantage_credit: number | null;
  payout_used: number | null;
  advantage: number | null;
  line_advantage: number | null;
}

export interface CkTradeinResponse {
  generated_at: string;
  params: {
    payout: 'cash' | 'credit';
    min_advantage: number;
    min_ck_price: number | null;
    top_n: number | null;
    statuses: string[];
    format: 'json';
  };
  summary: {
    scanned_rows: number;
    matched_rows: number;
    skipped_no_ck_buylist: number;
    skipped_no_manapool_price: number;
    copy_count: number;
    ck_payout_total: number;
    manapool_net_total: number;
    advantage_total: number;
  };
  total_cards: number;
  items: CkTradeinItem[];
}

export async function fetchCkTradein(params: CkTradeinParams = {}): Promise<CkTradeinResponse> {
  await ensureApiAuthenticated();
  const response = await api.get<CkTradeinResponse>('/ck-tradein/candidates', { params });
  return response.data;
}

/** Analysis CSV (`ck_tradein.csv`). This is not the Card Kingdom import file. */
export async function downloadCkTradeinCsv(params: CkTradeinParams = {}): Promise<void> {
  await ensureApiAuthenticated();

  const response = await api.get<Blob>('/ck-tradein/candidates', {
    params: {
      ...params,
      format: 'csv',
    },
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `ck-tradein_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export interface CkTradeinBatchLineInput {
  mtgjson_uuid: string;
  condition: string;
  printing: string;
  language: string;
  qty: number;
}

export interface CkTradeinBatchLine {
  id: string;
  mtgjson_uuid: string;
  name: string;
  set_name: string | null;
  card_number: string | null;
  condition: string;
  finish: string;
  is_foil: boolean;
  language: string;
  qty_requested: number;
  qty_pulled: number;
  sellable_qty?: number;
  shortfall_qty?: number;
  held_cost?: number;
  expected_unit_price: number;
  line_total: number;
  csv_part_number?: number | null;
}

export interface CkTradeinBatch {
  id: string;
  status: string;
  payout_mode: 'cash' | 'credit';
  expected_total: number;
  actual_paid: number | null;
  variance: number | null;
  paid_at: string | null;
  shipped_at: string | null;
  csv_exported_at: string | null;
  csv_part_number: number | null;
  csv_part_count: number | null;
  created_at: string;
  updated_at: string;
  line_count: number;
  lines: CkTradeinBatchLine[];
}

export interface CkTradeinBatchSummary {
  id: string;
  status: string;
  payout_mode: 'cash' | 'credit';
  expected_total: number;
  actual_paid: number | null;
  variance: number | null;
  paid_at: string | null;
  shipped_at: string | null;
  csv_exported_at: string | null;
  csv_part_number: number | null;
  csv_part_count: number | null;
  created_at: string;
  updated_at: string;
  line_count: number;
  label: string;
}

export async function listOpenCkTradeinBatches(): Promise<CkTradeinBatchSummary[]> {
  await ensureApiAuthenticated();
  const response = await api.get<{ batches: CkTradeinBatchSummary[] }>('/ck-tradein/batches');
  return response.data.batches;
}

export async function createCkTradeinBatch(body: {
  payout_mode: 'cash' | 'credit';
  lines: CkTradeinBatchLineInput[];
}): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const response = await api.post<CkTradeinBatch>('/ck-tradein/batches', body);
  return response.data;
}

export async function fetchCkTradeinBatch(batchId: string): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const response = await api.get<CkTradeinBatch>(`/ck-tradein/batches/${batchId}`);
  return response.data;
}

export interface CkTradeinPickListLine {
  id: string;
  mtgjson_uuid: string;
  name: string;
  set_name: string | null;
  card_number: string | null;
  condition: string;
  finish: string;
  is_foil: boolean;
  language: string;
  qty_requested: number;
  locations: string[];
  in_ck_file: boolean;
  ck_file_note: string | null;
}

export interface CkTradeinPickList {
  batch_id: string;
  generated_at: string;
  lines: CkTradeinPickListLine[];
}

/** Step-2 pick list. Viewing it does not change listings or copy status. */
export async function fetchCkTradeinPickList(batchId: string): Promise<CkTradeinPickList> {
  await ensureApiAuthenticated();
  const response = await api.get<CkTradeinPickList>(`/ck-tradein/batches/${batchId}/pick-list`);
  return response.data;
}

export async function updateCkTradeinLine(
  batchId: string,
  lineId: string,
  qtyRequested: number,
): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const response = await api.patch<CkTradeinBatch>(
    `/ck-tradein/batches/${batchId}/lines/${lineId}`,
    { qty_requested: qtyRequested },
  );
  return response.data;
}

export async function removeCkTradeinLine(batchId: string, lineId: string): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const response = await api.delete<CkTradeinBatch>(`/ck-tradein/batches/${batchId}/lines/${lineId}`);
  return response.data;
}

async function postLineQty(
  batchId: string,
  lineId: string,
  action: 'pull' | 'undo' | 'return',
  qty: number,
): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const response = await api.post<CkTradeinBatch>(
    `/ck-tradein/batches/${batchId}/lines/${lineId}/${action}`,
    { qty },
  );
  return response.data;
}

/** Pin oldest sellable copies and drop channel quantity. */
export function markCkTradeinLinePulled(
  batchId: string,
  lineId: string,
  qty: number,
): Promise<CkTradeinBatch> {
  return postLineQty(batchId, lineId, 'pull', qty);
}

/** Restore the prior status. The API rejects this after ship. */
export function undoCkTradeinLinePull(
  batchId: string,
  lineId: string,
  qty: number,
): Promise<CkTradeinBatch> {
  return postLineQty(batchId, lineId, 'undo', qty);
}

/** Explicit restock of held copies back to in-stock. Optional note is stored on the pins. */
export async function returnCkTradeinLineToStock(
  batchId: string,
  lineId: string,
  qty: number,
  note?: string,
): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const trimmed = note?.trim();
  const response = await api.post<CkTradeinBatch>(
    `/ck-tradein/batches/${batchId}/lines/${lineId}/return`,
    trimmed ? { qty, note: trimmed } : { qty },
  );
  return response.data;
}

/** Freeze the batch. Shipping does not change inventory. */
export async function markCkTradeinBatchShipped(
  batchId: string,
  shippedAt?: string,
): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const response = await api.post<CkTradeinBatch>(
    `/ck-tradein/batches/${batchId}/ship`,
    shippedAt ? { shipped_at: shippedAt } : {},
  );
  return response.data;
}

/** One cash or store-credit total for the batch. Not split across cards. */
export async function recordCkTradeinPayout(
  batchId: string,
  body: {
    actual_paid: number;
    paid_at?: string;
    payout_mode: 'cash' | 'credit';
  },
): Promise<CkTradeinBatch> {
  await ensureApiAuthenticated();
  const response = await api.post<CkTradeinBatch>(`/ck-tradein/batches/${batchId}/payout`, body);
  return response.data;
}

export interface CkTradeinCkCsvPart {
  part_number: number;
  filename: string;
  line_count: number;
  card_count: number;
  csv: string;
}

export interface CkTradeinCkCsvSkipped {
  line_id: string;
  name: string;
  set_name: string | null;
  reason: string;
  detail: string;
}

export interface CkTradeinCkCsvCollision {
  title: string;
  edition: string;
  foil: boolean;
  mtgjson_uuids: string[];
  line_ids: string[];
  message: string;
}

export interface CkTradeinCkCsvEditionWarning {
  line_id: string;
  title: string;
  edition: string;
  message: string;
}

export interface CkTradeinCkCsvExport {
  batch_id: string;
  qty_source: 'requested' | 'pulled' | 'mixed';
  qty_label: string | null;
  csv_exported_at: string;
  csv_part_number: number | null;
  csv_part_count: number | null;
  parts: CkTradeinCkCsvPart[];
  skipped: CkTradeinCkCsvSkipped[];
  collisions: CkTradeinCkCsvCollision[];
  edition_warnings: CkTradeinCkCsvEditionWarning[];
  line_parts: Array<{
    line_id: string;
    csv_part_number: number | null;
    csv_part_numbers: number[];
    qty: number;
    qty_source: string;
    included: boolean;
  }>;
  batch: CkTradeinBatch;
}

/** Sell-to-CK import files. Not the step-1 analysis CSV. */
export async function exportCkTradeinCsv(batchId: string): Promise<CkTradeinCkCsvExport> {
  await ensureApiAuthenticated();
  const response = await api.post<CkTradeinCkCsvExport>(`/ck-tradein/batches/${batchId}/ck-csv`);
  return response.data;
}

export function saveCkCsvPart(filename: string, csvText: string): void {
  const url = window.URL.createObjectURL(new Blob([csvText], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
